import { useState, useMemo, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, X, Search, ChevronDown } from 'lucide-react';

export default function EngineeringWorklogs({ 
  worklogsData = [], 
  requestsData = [], 
  tasksData = [], 
  usersData = [],
  loading 
}) {
  // Load saved filters from sessionStorage
  const loadSavedFilters = () => {
    const saved = sessionStorage.getItem('engineeringWorklogsFilters');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {};
      }
    }
    return {};
  };

  const savedFilters = loadSavedFilters();

  // State for filters
  const [searchTerm, setSearchTerm] = useState(savedFilters.searchTerm || '');
  const [selectedUsers, setSelectedUsers] = useState(savedFilters.selectedUsers || []);
  const [selectedYears, setSelectedYears] = useState(savedFilters.selectedYears || []);
  const [selectedRequests, setSelectedRequests] = useState(savedFilters.selectedRequests || []);
  const [selectedTasks, setSelectedTasks] = useState(savedFilters.selectedTasks || []);
  
  // State for dropdown visibility
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showRequestDropdown, setShowRequestDropdown] = useState(false);
  const [showTaskDropdown, setShowTaskDropdown] = useState(false);
  
  // State for search within dropdowns
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [yearSearchTerm, setYearSearchTerm] = useState('');
  const [requestSearchTerm, setRequestSearchTerm] = useState('');
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    const filters = {
      searchTerm,
      selectedUsers,
      selectedYears,
      selectedRequests,
      selectedTasks
    };
    sessionStorage.setItem('engineeringWorklogsFilters', JSON.stringify(filters));
  }, [searchTerm, selectedUsers, selectedYears, selectedRequests, selectedTasks]);

  // Refs for dropdown click outside detection
  const userDropdownRef = useRef(null);
  const yearDropdownRef = useRef(null);
  const requestDropdownRef = useRef(null);
  const taskDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target)) {
        setShowYearDropdown(false);
      }
      if (requestDropdownRef.current && !requestDropdownRef.current.contains(event.target)) {
        setShowRequestDropdown(false);
      }
      if (taskDropdownRef.current && !taskDropdownRef.current.contains(event.target)) {
        setShowTaskDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Parse duration string to hours (e.g., "1 day 04:25:00" or "01:30:00" or "1h 0m")
  const parseDuration = (worklog) => {
    // Always use duration field first
    const durationValue = worklog.duration || worklog.timespent || worklog.time_decimal || worklog.hours || worklog.time_spent || worklog.duration_decimal;
    
    if (!durationValue) {
      return 0;
    }
    
    // If it's already a number, return it
    if (typeof durationValue === 'number') {
      return durationValue;
    }
    
    // If it's an object (PostgreSQL interval), try to extract hours
    if (typeof durationValue === 'object' && durationValue !== null) {
      // PostgreSQL interval object might have hours, minutes, seconds properties
      const hours = durationValue.hours || 0;
      const minutes = durationValue.minutes || 0;
      const seconds = durationValue.seconds || 0;
      const days = durationValue.days || 0;
      return days * 24 + hours + minutes / 60 + seconds / 3600;
    }
    
    // Convert to string if it's not already
    const str = String(durationValue);
    
    let totalHours = 0;
    
    // Parse "1h 0m" or "2h 30m" format
    const hourMinMatch = str.match(/(\d+)h\s*(\d+)m/i);
    if (hourMinMatch) {
      totalHours = parseInt(hourMinMatch[1]) + parseInt(hourMinMatch[2]) / 60;
      return totalHours;
    }
    
    // Parse "X day" format
    const dayMatch = str.match(/(\d+)\s*day/i);
    if (dayMatch) {
      totalHours += parseInt(dayMatch[1]) * 24;
    }
    
    // Parse "HH:MM:SS" format
    const timeMatch = str.match(/(\d{1,2}):(\d{2}):(\d{2})/);
    if (timeMatch) {
      totalHours += parseInt(timeMatch[1]);
      totalHours += parseInt(timeMatch[2]) / 60;
      totalHours += parseInt(timeMatch[3]) / 3600;
    }
    
    return totalHours;
  };

  // Debug: Log worklog structure
  useEffect(() => {
    if (worklogsData.length > 0) {
      console.log('First worklog:', worklogsData[0]);
      console.log('Worklog keys:', Object.keys(worklogsData[0]));
      console.log('Duration fields check:', {
        timespent: worklogsData[0].timespent,
        duration: worklogsData[0].duration,
        time_decimal: worklogsData[0].time_decimal,
        hours: worklogsData[0].hours,
        time_spent: worklogsData[0].time_spent
      });
      console.log('Parsed hours:', parseDuration(worklogsData[0]));
    }
  }, [worklogsData]);

  // Create lookup maps for efficient data retrieval
  const taskLookup = useMemo(() => {
    const map = new Map();
    tasksData.forEach(task => map.set(task.id, task));
    return map;
  }, [tasksData]);

  const requestLookup = useMemo(() => {
    const map = new Map();
    requestsData.forEach(req => map.set(req.id, req));
    return map;
  }, [requestsData]);

  const userLookup = useMemo(() => {
    const map = new Map();
    usersData.forEach(user => map.set(user.id, user));
    return map;
  }, [usersData]);

  // Get worklog details with task and request relationships
  const getWorklogDetails = (worklog) => {
    const task = taskLookup.get(worklog.object_id);
    const request = task ? requestLookup.get(task.object_id) : null;
    const user = userLookup.get(worklog.created_by_id);
    return { task, request, user };
  };

  // Filter helpers
  const toggleSelection = (type, value) => {
    const setters = {
      users: setSelectedUsers,
      years: setSelectedYears,
      requests: setSelectedRequests,
      tasks: setSelectedTasks,
    };
    const setter = setters[type];
    setter(prev => 
      prev.includes(value) 
        ? prev.filter(item => item !== value)
        : [...prev, value]
    );
  };

  const removeSelection = (type, value) => {
    const setters = {
      users: setSelectedUsers,
      years: setSelectedYears,
      requests: setSelectedRequests,
      tasks: setSelectedTasks,
    };
    setters[type](prev => prev.filter(item => item !== value));
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedUsers([]);
    setSelectedYears([]);
    setSelectedRequests([]);
    setSelectedTasks([]);
    setUserSearchTerm('');
    setRequestSearchTerm('');
    setTaskSearchTerm('');
  };

  const activeFilterCount = selectedUsers.length + selectedYears.length + 
    selectedRequests.length + selectedTasks.length;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedUsers, selectedYears, selectedRequests, selectedTasks]);

  // Filter worklogs based on all criteria
  const filteredWorklogs = useMemo(() => {
    return worklogsData.filter(log => {
      // Search filter
      if (searchTerm && !log.description?.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      // User filter
      if (selectedUsers.length > 0 && !selectedUsers.includes(log.created_by_id)) return false;
      
      // Year filter
      if (selectedYears.length > 0) {
        const logYear = log.start_date ? new Date(log.start_date).getFullYear() : null;
        if (!logYear || !selectedYears.includes(logYear)) return false;
      }
      
      const task = taskLookup.get(log.object_id);
      
      // Request filter
      if (selectedRequests.length > 0) {
        const requestId = task?.object_id;
        if (!requestId || !selectedRequests.includes(requestId)) return false;
      }
      
      // Task filter
      if (selectedTasks.length > 0 && !selectedTasks.includes(log.object_id)) return false;
      
      return true;
    });
  }, [worklogsData, searchTerm, selectedUsers, selectedYears, selectedRequests, selectedTasks, taskLookup]);

  // Get unique years
  const years = useMemo(() => {
    const yearsSet = new Set(
      worklogsData
        .map(log => log.start_date ? new Date(log.start_date).getFullYear() : null)
        .filter(Boolean)
    );
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [worklogsData]);

  // Filtered years for search
  const filteredYears = useMemo(() => {
    if (!yearSearchTerm) return years;
    return years.filter(year => 
      year.toString().includes(yearSearchTerm)
    );
  }, [years, yearSearchTerm]);

  // Get active users
  const activeUsers = useMemo(() => {
    const userIds = new Set(worklogsData.map(log => log.created_by_id).filter(Boolean));
    return usersData.filter(user => userIds.has(user.id));
  }, [worklogsData, usersData]);

  // Filtered users for search
  const filteredUsers = useMemo(() => {
    if (!userSearchTerm) return activeUsers;
    return activeUsers.filter(user => 
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(userSearchTerm.toLowerCase())
    );
  }, [activeUsers, userSearchTerm]);

  // Get active requests
  const activeRequests = useMemo(() => {
    const requestIds = new Set();
    worklogsData.forEach(log => {
      const task = taskLookup.get(log.object_id);
      if (task?.object_id) requestIds.add(task.object_id);
    });
    return requestsData.filter(req => requestIds.has(req.id));
  }, [worklogsData, requestsData, taskLookup]);

  // Filtered requests for search
  const filteredRequests = useMemo(() => {
    if (!requestSearchTerm) return activeRequests;
    return activeRequests.filter(req => 
      (req.number?.toLowerCase().includes(requestSearchTerm.toLowerCase())) ||
      (req.name?.toLowerCase().includes(requestSearchTerm.toLowerCase())) ||
      (req.eng_project_number?.toLowerCase().includes(requestSearchTerm.toLowerCase()))
    );
  }, [activeRequests, requestSearchTerm]);

  // Get active tasks
  const activeTasks = useMemo(() => {
    const taskIds = new Set(worklogsData.map(log => log.object_id).filter(Boolean));
    return tasksData.filter(task => taskIds.has(task.id));
  }, [worklogsData, tasksData]);

  // Filtered tasks for search
  const filteredTasks = useMemo(() => {
    if (!taskSearchTerm) return activeTasks;
    return activeTasks.filter(task => 
      (task.number?.toLowerCase().includes(taskSearchTerm.toLowerCase())) ||
      (task.description?.toLowerCase().includes(taskSearchTerm.toLowerCase()))
    );
  }, [activeTasks, taskSearchTerm]);

  // Calculate statistics
  const totalHours = useMemo(() => {
    return filteredWorklogs.reduce((sum, log) => sum + parseDuration(log), 0);
  }, [filteredWorklogs]);

  const uniqueUsers = useMemo(() => {
    const userIds = new Set(filteredWorklogs.map(log => log.created_by_id));
    return Array.from(userIds);
  }, [filteredWorklogs]);

  const avgDuration = useMemo(() => {
    if (filteredWorklogs.length === 0) return 0;
    return totalHours / filteredWorklogs.length;
  }, [totalHours, filteredWorklogs]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredWorklogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredWorklogs.length);
  const paginatedWorklogs = filteredWorklogs.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const durationRanges = [
    { value: '0-1', label: '0-1 hours' },
    { value: '1-4', label: '1-4 hours' },
    { value: '4-8', label: '4-8 hours' },
    { value: '8+', label: '8+ hours' },
  ];

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading engineering worklogs...</p>
        </div>
      </div>
    );
  }

  if (!worklogsData || worklogsData.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold mb-2">Engineering Worklogs</h1>
        <p className="text-gray-600">No worklog data available.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex">
      {/* Filters Sidebar */}
      <div className="w-80 bg-white border-r shadow-sm overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-2">Filters</h2>
          <p className="text-sm text-gray-500 mb-6">
            Showing {filteredWorklogs.length.toLocaleString()} of {worklogsData.length.toLocaleString()} worklogs
          </p>

          <div className="space-y-6">
            {/* Search */}
            <div className="space-y-2">
              <Label htmlFor="search">Search Description</Label>
              <Input
                id="search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search worklogs..."
              />
            </div>

            {/* Active Filters Display */}
            {activeFilterCount > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Active Filters ({activeFilterCount})</Label>
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Clear All
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map(userId => {
                    const user = usersData.find(u => u.id === userId);
                    return (
                      <Badge key={userId} variant="secondary" className="flex items-center gap-1 border-l-2 border-blue-500">
                        {user ? `${user.first_name} ${user.last_name}` : userId}
                        <X 
                          className="h-3 w-3 cursor-pointer hover:text-destructive" 
                          onClick={() => removeSelection('users', userId)}
                        />
                      </Badge>
                    );
                  })}
                  {selectedYears.map(year => (
                    <Badge key={year} variant="secondary" className="flex items-center gap-1 border-l-2 border-green-500">
                      {year}
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-destructive" 
                        onClick={() => removeSelection('years', year)}
                      />
                    </Badge>
                  ))}
                  {selectedRequests.map(requestId => {
                    const request = requestsData.find(r => r.id === requestId);
                    return (
                      <Badge key={requestId} variant="secondary" className="flex items-center gap-1 border-l-2 border-orange-500">
                        {request?.eng_project_number ? `${request.eng_project_number} - ${request.number}` : request?.number || requestId}
                        <X 
                          className="h-3 w-3 cursor-pointer hover:text-destructive" 
                          onClick={() => removeSelection('requests', requestId)}
                        />
                      </Badge>
                    );
                  })}
                  {selectedTasks.map(taskId => {
                    const task = tasksData.find(t => t.id === taskId);
                    return (
                      <Badge key={taskId} variant="secondary" className="flex items-center gap-1 border-l-2 border-pink-500">
                        {task?.number || taskId}
                        <X 
                          className="h-3 w-3 cursor-pointer hover:text-destructive" 
                          onClick={() => removeSelection('tasks', taskId)}
                        />
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Users Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Users</Label>
              <div className="relative" ref={userDropdownRef}>
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                >
                  <span className="text-gray-700">
                    {selectedUsers.length > 0 ? `${selectedUsers.length} selected` : 'Select users'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
                {showUserDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <Input
                          type="text"
                          value={userSearchTerm}
                          onChange={(e) => setUserSearchTerm(e.target.value)}
                          placeholder="Search users..."
                          className="pl-8"
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredUsers.map(user => (
                        <div
                          key={user.id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                          onClick={() => toggleSelection('users', user.id)}
                        >
                          <Checkbox checked={selectedUsers.includes(user.id)} />
                          <span className="text-sm">{user.first_name} {user.last_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Years Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Years</Label>
              <div className="relative" ref={yearDropdownRef}>
                <button
                  onClick={() => setShowYearDropdown(!showYearDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                >
                  <span className="text-gray-700">
                    {selectedYears.length > 0 ? `${selectedYears.length} selected` : 'Select years'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
                {showYearDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden flex flex-col">
                    <div className="p-2 border-b sticky top-0 bg-white">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <Input
                          type="text"
                          placeholder="Search years..."
                          value={yearSearchTerm}
                          onChange={(e) => setYearSearchTerm(e.target.value)}
                          className="pl-8 h-9 text-sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-52">
                      {filteredYears.map(year => (
                        <div
                          key={year}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                          onClick={() => toggleSelection('years', year)}
                        >
                          <Checkbox checked={selectedYears.includes(year)} />
                          <span className="text-sm">{year}</span>
                        </div>
                      ))}
                      {filteredYears.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">No years found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Requests Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Requests</Label>
              <div className="relative" ref={requestDropdownRef}>
                <button
                  onClick={() => setShowRequestDropdown(!showRequestDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                >
                  <span className="text-gray-700">
                    {selectedRequests.length > 0 ? `${selectedRequests.length} selected` : 'Select requests'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
                {showRequestDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <Input
                          type="text"
                          value={requestSearchTerm}
                          onChange={(e) => setRequestSearchTerm(e.target.value)}
                          placeholder="Search requests..."
                          className="pl-8"
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredRequests.map(request => (
                        <div
                          key={request.id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                          onClick={() => toggleSelection('requests', request.id)}
                        >
                          <Checkbox checked={selectedRequests.includes(request.id)} />
                          <span className="text-sm">
                            {request.eng_project_number ? `${request.eng_project_number} - ${request.number}` : request.number}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tasks Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Tasks</Label>
              <div className="relative" ref={taskDropdownRef}>
                <button
                  onClick={() => setShowTaskDropdown(!showTaskDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm bg-white hover:bg-gray-50"
                >
                  <span className="text-gray-700">
                    {selectedTasks.length > 0 ? `${selectedTasks.length} selected` : 'Select tasks'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
                {showTaskDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-hidden">
                    <div className="p-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <Input
                          type="text"
                          value={taskSearchTerm}
                          onChange={(e) => setTaskSearchTerm(e.target.value)}
                          placeholder="Search tasks..."
                          className="pl-8"
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredTasks.map(task => (
                        <div
                          key={task.id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                          onClick={() => toggleSelection('tasks', task.id)}
                        >
                          <Checkbox checked={selectedTasks.includes(task.id)} />
                          <span className="text-sm">{task.number}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b bg-white">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold mb-2">Engineering Worklogs</h1>
              <p className="text-gray-600">Track time spent on engineering requests and tasks</p>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="p-6 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="border-l-4 border-blue-500">
                <CardTitle className="text-sm font-medium text-gray-600">TOTAL WORKLOGS</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{filteredWorklogs.length.toLocaleString()}</div>
                <p className="text-xs text-gray-500 mt-1">of {worklogsData.length.toLocaleString()} total</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="border-l-4 border-green-500">
                <CardTitle className="text-sm font-medium text-gray-600">TOTAL HOURS</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{totalHours.toLocaleString()}</div>
                <p className="text-xs text-gray-500 mt-1">hours logged</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="border-l-4 border-purple-500">
                <CardTitle className="text-sm font-medium text-gray-600">ACTIVE USERS</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{uniqueUsers.length}</div>
                <p className="text-xs text-gray-500 mt-1">contributors</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="border-l-4 border-orange-500">
                <CardTitle className="text-sm font-medium text-gray-600">AVG. DURATION</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{avgDuration.toFixed(1)}</div>
                <p className="text-xs text-gray-500 mt-1">hours per worklog</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Table Container */}
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-white rounded-lg shadow">
            {/* Table Header with Pagination Controls */}
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Rows per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1} to {endIndex} of {filteredWorklogs.length.toLocaleString()} results
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedWorklogs.map((worklog, index) => {
                    const { task, request, user } = getWorklogDetails(worklog);
                    const hours = parseDuration(worklog);
                    return (
                      <tr key={worklog.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {worklog.start_date ? new Date(worklog.start_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {user ? `${user.first_name} ${user.last_name}` : worklog.created_by_id}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="max-w-md truncate" title={worklog.description}>
                            {worklog.description}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {task ? (
                            <Badge variant="outline" className="border-blue-300 text-blue-700">
                              {task.number}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {request ? (
                            <Badge variant="outline" className="border-green-300 text-green-700">
                              {request.eng_project_number ? `${request.eng_project_number} - ${request.number}` : request.number}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{hours.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="p-4 border-t flex items-center justify-center gap-2">
              <button
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={`px-3 py-1 text-sm border rounded ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
